package com.fantasytenis.shared.data.contests.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ===== PERFECT LINEUP DTOs =====

/**
 * Perfect lineup slot - optimal player selection for a category
 */
@Serializable
data class PerfectLineupSlotDto(
    @SerialName("playerId") val playerId: String,
    @SerialName("playerName") val playerName: String,
    val cost: Int,
    val points: Double
)

/**
 * Perfect lineup - calculated after contest completes
 * Shows the optimal player selection that would have maximized fantasy points
 */
@Serializable
data class PerfectLineupDto(
    val serve: PerfectLineupSlotDto,
    val forehand: PerfectLineupSlotDto,
    val backhand: PerfectLineupSlotDto,
    val volleys: PerfectLineupSlotDto,
    val fighting: PerfectLineupSlotDto,
    @SerialName("totalCost") val totalCost: Int,
    @SerialName("totalPoints") val totalPoints: Double,
    @SerialName("calculatedAt") val calculatedAt: String
)

// ===== CONTEST DTOs =====

@Serializable
data class ContestDto(
    val id: String,
    val name: String,
    @SerialName("slateId") val slateId: String = "",
    val type: Int = 1,
    val status: Int = 1,
    @SerialName("entryFee") val entryFee: Int = 0,
    @SerialName("prizePool") val prizePool: Int = 0,
    @SerialName("maxEntries") val maxEntries: Int = 100,
    @SerialName("currentEntries") val currentEntries: Int = 0,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("salaryCap") val salaryCap: Int = 35,
    @SerialName("lineupLockTime") val lineupLockTime: String = "",
    @SerialName("startTime") val startTime: String = "",
    @SerialName("payoutStructure") val payoutStructure: String? = null, // JSON string
    @SerialName("isFeatured") val isFeatured: Boolean = false,
    @SerialName("isGuaranteed") val isGuaranteed: Boolean = true,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = "",
    // Lock override fields for admin manual control
    @SerialName("lockOverride") val lockOverride: Int = 1, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    @SerialName("lockOverrideBy") val lockOverrideBy: String = "",
    @SerialName("lockOverrideAt") val lockOverrideAt: String = "",
    @SerialName("lockOverrideReason") val lockOverrideReason: String = "",
    @SerialName("isManuallyUnlocked") val isManuallyUnlocked: Boolean = false,
    // Perfect lineup (calculated after contest completes)
    @SerialName("perfectLineup") val perfectLineup: PerfectLineupDto? = null
)

@Serializable
data class ContestEntryDto(
    val id: String,
    @SerialName("contestId") val contestId: String,
    @SerialName("userId") val userId: String,
    @SerialName("lineupId") val lineupId: String? = null,
    val status: Int = 1,
    @SerialName("totalPoints") val totalPoints: Double = 0.0,
    val rank: Int? = null,
    @SerialName("payoutAmount") val payoutAmount: Int = 0,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = "",
    // User profile fields (enriched by gateway)
    @SerialName("userName") val userName: String? = null,
    @SerialName("displayName") val displayName: String? = null,
    @SerialName("username") val username: String? = null,
    @SerialName("avatarUrl") val avatarUrl: String? = null
)

@Serializable
data class PayoutTierDto(
    @SerialName("minRank") val minRank: Int,
    @SerialName("maxRank") val maxRank: Int,
    val amount: Int? = null,
    val percentage: Double? = null
)

// ===== REQUEST DTOs =====

@Serializable
data class CreateContestRequest(
    val name: String,
    @SerialName("slateId") val slateId: String,
    val type: Int,
    @SerialName("entryFee") val entryFee: Int,
    @SerialName("prizePool") val prizePool: Int,
    @SerialName("maxEntries") val maxEntries: Int,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("salaryCap") val salaryCap: Int = 35,
    @SerialName("lineupLockTime") val lineupLockTime: String,
    @SerialName("startTime") val startTime: String,
    @SerialName("payoutStructure") val payoutStructure: List<PayoutTierDto>? = null
)

// ===== RESPONSE DTOs =====

@Serializable
data class ContestApiResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: T? = null
)

@Serializable
data class ContestListResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: List<T>? = null,
    val total: Int = 0
)

// ===== SLATE DTOs =====

@Serializable
data class SlateDto(
    val id: String,
    val name: String,
    val description: String = "",
    val type: String = "",
    val status: String = "",
    @SerialName("tournamentId") val tournamentId: String = "",
    @SerialName("tournamentName") val tournamentName: String = "",
    @SerialName("startTime") val startTime: String = "",
    @SerialName("lockTime") val lockTime: String = "",
    @SerialName("endTime") val endTime: String = "",
    @SerialName("matchCount") val matchCount: Int = 0,
    @SerialName("playerCount") val playerCount: Int = 0,
    @SerialName("defaultSalaryCap") val defaultSalaryCap: Int = 35,
    val gender: String = "M",
    @SerialName("isLocked") val isLocked: Boolean = false,
    @SerialName("canEnter") val canEnter: Boolean = true
)

@Serializable
data class SlatePlayerDto(
    @SerialName("playerId") val playerId: String,
    val name: String,
    val nationality: String? = null,
    val ranking: Int? = null,
    @SerialName("serveCost") val serveCost: Int,
    @SerialName("forehandCost") val forehandCost: Int,
    @SerialName("backhandCost") val backhandCost: Int,
    @SerialName("volleysCost") val volleysCost: Int,
    @SerialName("fightingCost") val fightingCost: Int,
    @SerialName("isActive") val isActive: Boolean = true,
    // Match context fields (populated when fetching slate players)
    @SerialName("matchId") val matchId: String? = null,
    @SerialName("opponentId") val opponentId: String? = null,
    @SerialName("opponentName") val opponentName: String? = null,
    @SerialName("opponentRanking") val opponentRanking: Int? = null,
    @SerialName("matchTime") val matchTime: String? = null
)

// ===== ADMIN LOCK OVERRIDE DTOs =====

@Serializable
data class SetContestLockOverrideRequest(
    @SerialName("lockOverride") val lockOverride: Int, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    val reason: String? = null
)

@Serializable
data class SetContestLockOverrideResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: ContestDto? = null
)

// ===== ADMIN DELETE ENTRY DTOs =====

@Serializable
data class AdminDeleteEntryRequest(
    val reason: String
)

@Serializable
data class AdminDeleteEntryResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: ContestEntryDto? = null,
    @SerialName("refundProcessed") val refundProcessed: Boolean = false,
    @SerialName("refundTransactionId") val refundTransactionId: String? = null
)
